import pluralize from 'pluralize';
import { useMemo, useState } from 'react';
import {
  ConsortiumAlertsSummary as ConsortiumAlertsSummaryData,
  ConsortiumAlertsSummaryAggregates,
  ConsortiumAlertsSummaryDispositionType,
} from 'app/modules/consortium/responses';
import {
  U21ButtonGroup,
  U21ButtonGroupButtonProps,
  U21Chip,
  U21ChipColor,
  U21Spacer,
  U21Table,
  U21TableBodyCellProps,
  U21TableColumn,
  U21Typography,
} from 'app/shared/u21-ui/components';

const CONSORTIUM_DATE_RANGE_BUTTONS: U21ButtonGroupButtonProps<
  keyof ConsortiumAlertsSummaryData
>[] = [
  {
    label: '7 Days',
    value: 'last_7_days',
  },
  {
    label: '30 Days',
    value: 'last_30_days',
  },
  {
    label: '90 Days',
    value: 'last_90_days',
  },
  {
    label: 'All Time',
    value: 'all_time',
  },
];

type ConsortiumAlertsSummaryTableData = ConsortiumAlertsSummaryAggregates & {
  disposition_type: ConsortiumAlertsSummaryDispositionType | 'Total';
};

const CONSORTIUM_DISPOSITION_KEYS: ConsortiumAlertsSummaryDispositionType[] = [
  'true_positive',
  'under_review',
  'open',
  'false_positive',
];

const CONSORTIUM_DISPOSITION_TYPE_LABELS: Record<
  ConsortiumAlertsSummaryDispositionType,
  string
> = {
  true_positive: 'Confirmed unusual',
  under_review: 'Escalated',
  false_positive: 'False positive',
  open: 'Under investigation',
};

const CONSORTIUM_DISPOSITION_TYPE_TOOLTIP: Record<
  ConsortiumAlertsSummaryDispositionType,
  string
> = {
  true_positive: 'Alert was marked as a true positive within Unit21.',
  under_review: 'Alert has been escalated to high priority investigation.',
  false_positive: 'Alert was marked as a false positive within Unit21.',
  open: 'Alert is open and being reviewed.',
};

const CONSORTIUM_DISPOSITION_TYPE_COLORS: Record<
  ConsortiumAlertsSummaryDispositionType,
  U21ChipColor
> = {
  true_positive: 'error',
  under_review: 'warning',
  false_positive: 'success',
  open: 'info',
};

const CONSORTIUM_ALERTS_SUMMARY_TABLE_COLUMNS: U21TableColumn<ConsortiumAlertsSummaryTableData>[] =
  [
    {
      id: 'disposition_type',
      accessor: 'disposition_type',
      Header: 'Status',
      Cell: (
        props: U21TableBodyCellProps<
          ConsortiumAlertsSummaryTableData,
          ConsortiumAlertsSummaryTableData['disposition_type'] | undefined
        >,
      ) => {
        const { value } = props;
        if (!value) {
          return null;
        }
        if (value === 'Total') {
          return <U21Typography variant="subtitle2">{value}</U21Typography>;
        }
        return (
          <U21Chip
            variant="ghost"
            color={CONSORTIUM_DISPOSITION_TYPE_COLORS[value]}
            tooltip={CONSORTIUM_DISPOSITION_TYPE_TOOLTIP[value]}
          >
            {CONSORTIUM_DISPOSITION_TYPE_LABELS[value]}
          </U21Chip>
        );
      },
      disableSortBy: true,
      width: 100,
    },
    {
      id: 'count',
      accessor: 'count',
      Header: 'Count',
      disableSortBy: true,
      width: 20,
    },
    {
      id: 'distinct_orgs',
      accessor: 'distinct_orgs',
      Header: 'Org count',
      disableSortBy: true,
      width: 20,
    },
    {
      id: 'org_types',
      accessor: 'org_types',
      Header: 'Org types',
      type: 'list',
      disableSortBy: true,
      width: 100,
    },
  ];

interface ConsortiumAlertsSummaryProps {
  consortiumAlertsSummary: ConsortiumAlertsSummaryData;
}

export const ConsortiumAlertsSummary = ({
  consortiumAlertsSummary,
}: ConsortiumAlertsSummaryProps) => {
  // default to a date range that has true positive alerts
  const [selectedDateRange, setSelectedDateRange] = useState<
    keyof ConsortiumAlertsSummaryData
  >(
    () =>
      (
        Object.keys(
          consortiumAlertsSummary,
        ) as (keyof ConsortiumAlertsSummaryData)[]
      ).find((key) => consortiumAlertsSummary[key].true_positive.count > 0) ??
      'all_time',
  );

  const tableData: ConsortiumAlertsSummaryTableData[] = useMemo(() => {
    const summaryForRange = consortiumAlertsSummary[selectedDateRange];

    const rows: ConsortiumAlertsSummaryTableData[] =
      CONSORTIUM_DISPOSITION_KEYS.map((dispositionType) => {
        const dispositionSummary = summaryForRange[dispositionType];

        return {
          disposition_type: dispositionType,
          count: dispositionSummary.count,
          distinct_orgs: dispositionSummary.distinct_orgs,
          org_types: dispositionSummary.org_types,
        } satisfies ConsortiumAlertsSummaryTableData;
      });

    const total: ConsortiumAlertsSummaryTableData = {
      disposition_type: 'Total',
      count: 0,
      distinct_orgs: summaryForRange.total_distinct_orgs,
      org_types: [],
    };
    rows.forEach(({ count, org_types: orgTypes }) => {
      total.count += count;
      total.org_types = Array.from(new Set([...total.org_types, ...orgTypes]));
    });
    rows.push(total);
    return rows;
  }, [consortiumAlertsSummary, selectedDateRange]);
  return (
    <U21Spacer spacing={2}>
      <U21Spacer spacing={0.5}>
        <U21Typography variant="subtitle1">
          Consortium Alerts Summary
        </U21Typography>
        {consortiumAlertsSummary[selectedDateRange].true_positive.count > 0 && (
          <U21Typography variant="body2">
            Entity has{' '}
            <b>
              {pluralize(
                'confirmed unusual alert',
                consortiumAlertsSummary[selectedDateRange].true_positive.count,
                true,
              )}
            </b>{' '}
            across{' '}
            <b>
              {
                consortiumAlertsSummary[selectedDateRange].true_positive
                  .distinct_orgs
              }{' '}
              {consortiumAlertsSummary[selectedDateRange].true_positive
                .org_types.length > 1
                ? `${consortiumAlertsSummary[
                    selectedDateRange
                  ].true_positive.org_types
                    .slice(
                      0,
                      consortiumAlertsSummary[selectedDateRange].true_positive
                        .org_types.length - 1,
                    )
                    .join(', ')} and ${
                    consortiumAlertsSummary[selectedDateRange].true_positive
                      .org_types[
                      consortiumAlertsSummary[selectedDateRange].true_positive
                        .org_types.length - 1
                    ]
                  }`
                : (consortiumAlertsSummary[selectedDateRange].true_positive
                    .org_types[0] ?? '')}{' '}
              {pluralize(
                'organization',
                consortiumAlertsSummary[selectedDateRange].true_positive
                  .distinct_orgs,
              )}
            </b>{' '}
            {selectedDateRange === 'all_time' ? (
              ''
            ) : (
              <>
                in the last{' '}
                <b>
                  {selectedDateRange
                    .toString()
                    .replace('last_', ' ')
                    .replace('_', ' ')
                    .toLowerCase()}
                </b>
              </>
            )}{' '}
            in the Unit21 network.
          </U21Typography>
        )}
      </U21Spacer>
      <U21ButtonGroup
        size="small"
        buttons={CONSORTIUM_DATE_RANGE_BUTTONS}
        value={selectedDateRange}
        onClick={(value) => setSelectedDateRange(value)}
      />
      <U21Table
        columns={CONSORTIUM_ALERTS_SUMMARY_TABLE_COLUMNS}
        data={tableData}
      />
    </U21Spacer>
  );
};
